// Context/project Chip render helpers for TagChip.
import { Box, Chip } from '@mui/material';
import { TagChipLabelContent } from '@/components/ui/TagChipLabelContent';
import { tagChipProjectDotPx } from '@/components/ui/tagChipTruncate';

const FONT_WEIGHT_SEMIBOLD = 600;
const FONT_WEIGHT_REGULAR = 400;

function buildContextChipSx(baseSx, selected, colors) {
  return {
    ...baseSx,
    fontWeight: selected ? FONT_WEIGHT_SEMIBOLD : FONT_WEIGHT_REGULAR,
    ...(selected
      ? { borderColor: colors.bg, color: colors.bg, borderWidth: 2 }
      : {}),
  };
}

function buildProjectChipSx(baseSx, selected, selectable, colors) {
  const isOutlined = selectable && !selected;

  return {
    ...baseSx,
    fontWeight: selected ? FONT_WEIGHT_SEMIBOLD : FONT_WEIGHT_REGULAR,
    ...(isOutlined
      ? {
          bgcolor: 'transparent',
          color: 'text.secondary',
          borderColor: colors.bg,
        }
      : { bgcolor: colors.bg, color: colors.text, borderColor: colors.bg }),
  };
}

export function renderContextChip({
  prefixedLabel,
  colors,
  selected,
  baseSx,
  onClick,
  onDelete,
  testId,
  tabIndex,
  labelSuffix,
  dense,
  accessibleLabel,
  measureOnly,
}) {
  return (
    <Chip
      label={
        <TagChipLabelContent
          text={prefixedLabel}
          labelSuffix={labelSuffix}
          dense={dense}
        />
      }
      title={measureOnly ? undefined : accessibleLabel}
      aria-label={measureOnly ? undefined : accessibleLabel}
      aria-hidden={measureOnly || undefined}
      size="small"
      variant="outlined"
      data-tag-type="context"
      data-testid={testId}
      onClick={onClick}
      onDelete={onDelete}
      tabIndex={tabIndex}
      sx={buildContextChipSx(baseSx, selected, colors)}
    />
  );
}

export function renderProjectChip({
  prefixedLabel,
  colors,
  selected,
  selectable,
  baseSx,
  onClick,
  onDelete,
  testId,
  tabIndex,
  labelSuffix,
  dense,
  accessibleLabel,
  projectDisplayTier,
  measureOnly,
}) {
  const isOutlined = selectable && !selected;
  const isDot = projectDisplayTier === 'dot';
  const dotPx = tagChipProjectDotPx(dense);

  const chipLabel = isDot ? (
    <Box
      component="span"
      aria-hidden="true"
      data-testid="task-item__project-chip-dot"
      sx={{
        width: dotPx,
        height: dotPx,
        borderRadius: '50%',
        bgcolor: colors.bg,
        display: 'block',
        flexShrink: 0,
      }}
    />
  ) : (
    <TagChipLabelContent
      text={prefixedLabel}
      labelSuffix={labelSuffix}
      dense={dense}
    />
  );

  const dotSx = isDot
    ? {
        bgcolor: 'transparent',
        color: colors.text,
        borderColor: 'transparent',
        boxShadow: 'none',
      }
    : {};

  return (
    <Chip
      label={chipLabel}
      title={measureOnly ? undefined : accessibleLabel}
      aria-label={measureOnly ? undefined : accessibleLabel}
      aria-hidden={measureOnly || undefined}
      size="small"
      variant={isOutlined ? 'outlined' : 'filled'}
      data-tag-type="project"
      data-testid={measureOnly ? undefined : testId}
      onClick={onClick}
      onDelete={onDelete}
      tabIndex={tabIndex}
      sx={{
        ...buildProjectChipSx(baseSx, selected, selectable, colors),
        ...dotSx,
      }}
    />
  );
}
